/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import ProposalHeader from "./ProposalHeader";
import ProposalSectionRenderer from "./ProposalSectionRenderer";
import { proposalStylesheet } from "../theme/proposalTheme";

/**
 * ProposalPreview — renders a full proposal (header + sections + footer).
 *
 * Used both as the live builder preview pane and as the body of the
 * public hosted page. The renderer is intentionally stateless: it reads
 * `proposal.sections` verbatim and dispatches to ProposalSectionRenderer.
 */
export default function ProposalPreview({ proposal }) {
  // Inject stylesheet once per mount (idempotent — keyed by id).
  useEffect(() => {
    const id = "miq-proposal-stylesheet";
    if (document.getElementById(id)) return undefined;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = proposalStylesheet;
    document.head.appendChild(style);
    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, []);

  if (!proposal) return null;
  const sections = Array.isArray(proposal.sections) ? proposal.sections : [];
  const items = Array.isArray(proposal.items) ? proposal.items : [];
  const pricing = proposal.pricing || null;
  const hasPricing = items.length > 0 && pricing;

  return (
    <div className="miq-prop" data-testid="proposal-preview">
      <ProposalHeader proposal={proposal} />
      {sections.map((s) => (
        <ProposalSectionRenderer key={s.id} section={s} proposal={proposal} />
      ))}
      {hasPricing && <PricingBlock items={items} pricing={pricing} />}
    </div>
  );
}

function PricingBlock({ items, pricing }) {
  const currency = pricing?.currency || "USD";
  const fmt = (n) =>
    `${currency} ${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
  return (
    <section className="miq-section" data-section-kind="pricing" data-testid="proposal-pricing-block">
      <div className="miq-section--wide">
        <div className="eyebrow" style={{ marginBottom: 12 }}>Investment Breakdown</div>
        <h2 className="serif miq-h2" style={{ marginBottom: 28, maxWidth: 720 }}>
          A transparent, all-inclusive estimate
        </h2>
        <div className="miq-pricing-wrap">
          <div className="miq-pricing-scroll">
            <table className="miq-pricing-table">
              <colgroup>
                <col />
                <col style={{ width: 80 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 180 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Service</th>
                  <th className="num">Qty</th>
                  <th className="num">Unit Price</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>
                      <div className="miq-svc-name">{it.name}</div>
                      {it.description && <div className="miq-svc-desc">{it.description}</div>}
                    </td>
                    <td className="num">{Number(it.quantity || 0)}</td>
                    <td className="num">{fmt(it.unit_price)}</td>
                    <td className="num line-total">{fmt(it.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="subtotal">
                  <td colSpan={3} style={{ textAlign: "right" }}>Subtotal</td>
                  <td className="num">{fmt(pricing.subtotal)}</td>
                </tr>
                {(pricing.discount_amount || 0) > 0 && (
                  <tr className="discount">
                    <td colSpan={3} style={{ textAlign: "right" }}>
                      Discount{pricing.discount_percent > 0 ? ` (${pricing.discount_percent}%)` : ""}
                    </td>
                    <td className="num">− {fmt(pricing.discount_amount)}</td>
                  </tr>
                )}
                <tr className="grand-total">
                  <td colSpan={3} style={{ textAlign: "right" }}>Total Investment</td>
                  <td className="num">{fmt(pricing.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
